import React, { useEffect, useState, useRef } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, Loader2, Check, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FullSamplePacking = () => {
  const navigate = useNavigate();
  const [slips, setSlips] = useState([]);
  const [slipId, setSlipId] = useState('');
  const [slip, setSlip] = useState(null);
  const [destination, setDestination] = useState('');  // "agent:NAME" or "conf:NAME"
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [usnInput, setUsnInput] = useState('');
  const [summary, setSummary] = useState([]);
  const [scanning, setScanning] = useState(false);
  const usnRef = useRef(null);

  useEffect(() => {
    apiClient.get('/sample/pick-slips?sample_type=FULL').then((r) => {
      const ok = (r.data.slips || []).filter(
        (s) => ['PICKED', 'COMPLETED', 'PARTIALLY_PICKED'].includes(s.status),
      );
      setSlips(ok);
    });
  }, []);

  const loadSlip = async (id) => {
    setSlipId(id);
    const found = slips.find((s) => s.slip_id === id);
    setSlip(found);
    setDestination(''); setBundleBarcode(''); setUsnInput('');
    if (id) await refreshSummary(id);
  };

  const refreshSummary = async (id = slipId) => {
    if (!id) return;
    try {
      const r = await apiClient.get(`/sample/full-sample-pack/slip/${id}/summary`);
      setSummary(r.data.summary || []);
    } catch {}
  };

  useEffect(() => { if (slip && usnRef.current) usnRef.current.focus(); }, [slip]);

  const destinations = (() => {
    const set = new Map();
    slip?.items?.forEach((it) => {
      it.destinations?.forEach((d) => {
        const key = d.agent_name ? `agent:${d.agent_name}` : `conf:${d.conference_place}`;
        if (!set.has(key)) set.set(key, d);
      });
    });
    return Array.from(set.entries());
  })();

  const scan = async (e) => {
    e?.preventDefault?.();
    if (!destination) { toast.error('Pick a destination first'); return; }
    const [kind, name] = destination.split(':');
    const u = usnInput.trim();
    if (!u) return;
    setScanning(true);
    try {
      await apiClient.post('/sample/full-sample-pack/scan', {
        slip_id: slipId, usn_code: u,
        bundle_barcode: bundleBarcode || null,
        destination_agent: kind === 'agent' ? name : null,
        destination_conference: kind === 'conf' ? name : null,
      });
      toast.success(`${u} → ${name}`);
      setUsnInput('');
      await refreshSummary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Scan failed'); }
    finally { setScanning(false); }
  };

  return (
    <StaffScanGate stationName="Sample — Full Sample Packing">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="full-sample-packing">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/sample')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Full-Sample Packing</h1>
              <p className="text-sm text-[#E8DCC8]">Scan picked USNs → assign destination per piece</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Select Slip</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={slipId} onValueChange={loadSlip}>
              <SelectTrigger className="max-w-md" data-testid="full-slip-trigger">
                <SelectValue placeholder="Pick a FULL sample slip…" />
              </SelectTrigger>
              <SelectContent>
                {slips.length === 0 && <SelectItem disabled value="none">No slips ready</SelectItem>}
                {slips.map((s) => (
                  <SelectItem key={s.slip_id} value={s.slip_id}>
                    {s.pick_slip_number} — {s.total_qty} pcs ({s.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {slip && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pack Pieces</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Destination *</label>
                    <Select value={destination} onValueChange={setDestination}>
                      <SelectTrigger className="w-[260px] mt-1" data-testid="destination-trigger">
                        <SelectValue placeholder="Choose destination…" />
                      </SelectTrigger>
                      <SelectContent>
                        {destinations.map(([k, d]) => (
                          <SelectItem key={k} value={k}
                            data-testid={`destination-option-${k}`}>
                            {d.agent_name ? `Agent: ${d.agent_name}` : `Conf: ${d.conference_place}`}
                            {' · '}({d.qty})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Bundle barcode (optional)</label>
                    <Input value={bundleBarcode} onChange={(e) => setBundleBarcode(e.target.value)}
                      className="w-[200px] mt-1" placeholder="BUN-…"
                      data-testid="bundle-barcode-input" />
                  </div>
                </div>

                <form onSubmit={scan} className="flex items-center gap-2 pt-2 border-t">
                  <Scan className="w-4 h-4 text-gray-500" />
                  <Input ref={usnRef} value={usnInput}
                    onChange={(e) => setUsnInput(e.target.value)}
                    placeholder="Scan USN…" className="h-10 max-w-md"
                    disabled={!destination || scanning}
                    data-testid="pack-usn-input" />
                  <Button type="submit" disabled={scanning || !destination}
                    data-testid="pack-usn-submit">
                    {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pack'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Per-destination Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Destination</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead>Packed</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.map((s) => (
                      <TableRow key={`${s.kind}:${s.name}`}>
                        <TableCell className="text-xs">
                          <Badge variant="outline">{s.kind}</Badge> {s.name}
                        </TableCell>
                        <TableCell>{s.required}</TableCell>
                        <TableCell>{s.packed}</TableCell>
                        <TableCell>
                          {s.packed >= s.required
                            ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Done</Badge>
                            : <Badge variant="secondary">{s.required - s.packed} left</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
    </StaffScanGate>
  );
};

export default FullSamplePacking;
